import React, { useContext, useEffect } from "react";

import { useQuery } from "@apollo/client";
import { useParams, useSearchParams } from "react-router-dom";

import { Title } from "@olist/design-system";

import { buildCategoryTree } from "./utils";
import { Accordion } from "components/accordion";
import { AccordionSubHeader } from "components/accordion-sub-header";
import { Breadcrumb } from "components/breadcrumb";
import { ErrorHandling } from "components/errorHandling";
import { Loading } from "components/loading";
import { MixpanelContext } from "contexts";
import { productToTitle } from "helpers/formatters";
import { InternalServerError } from "icons/error";
import { getCategories } from "services/gql/getCategories";
import { CategoriesTree, ITreeAsCategories } from "types/articles";

const typeToText = {
  article: "Artigo",
  category: "Categoria",
  "sub-category": "Subcategoria",
};

export function Articles() {
  const params = useParams<{ product: string }>();
  const [searchParams] = useSearchParams();
  const { trackAgent } = useContext(MixpanelContext);

  const { data, error, loading } = useQuery(getCategories, {
    variables: {
      path: params.product,
    },
  }) as { data: ITreeAsCategories; error: unknown; loading: boolean };

  useEffect(() => {
    const product = window.location.pathname.split("/").at(-1);
    document.title = `Artigos ${
      productToTitle[product as string]
    } - Central de Ajuda`;
  }, []);

  if (loading) return <Loading />;
  if (error || data.pages.treeAsCategories.length === 0)
    return (
      <ErrorHandling
        image={<InternalServerError />}
        title="Algo inesperado aconteceu"
        subtitle="Ocorreu um erro inesperado ao buscar os artigos, mas fomos notificados!"
      />
    );

  const rootCategory = data.pages.treeAsCategories[0];
  const categories = buildCategoryTree(data);
  const categoryToOpen = searchParams.get("categoryPath");
  const accordionToBeOpen = `${params.product!}/${
    categoryToOpen?.split("/")[0]
  }`;
  const subAccordionToBeOpen = `${params.product!}/${categoryToOpen}`;

  const trackAction = (
    text: string,
    type: "article" | "category" | "sub-category"
  ) => {
    if (!Object.keys(productToTitle).includes(params.product ?? "")) return;
    const validatedProduct = params.product as keyof typeof productToTitle;

    trackAgent.track(`Central de ajuda - ${productToTitle[validatedProduct]}`, {
      asset: "Central de ajuda",
      page: "articles",
      component: type,
      product: productToTitle[validatedProduct],
      clickedOn: `${typeToText[type]} - ${text}`,
    });
  };

  const renderAccordions = (item: CategoriesTree) => {
    if (item.isFolder) {
      const isPreviousOpen = item.path === accordionToBeOpen;
      return (
        <Accordion
          key={item.id}
          title={item.title}
          isPreviousOpen={isPreviousOpen}
          onClickTrack={() => trackAction(item.title, "category")}
        >
          <AccordionSubHeader
            key={`accordion-subheader-${item.path}-${item.id}`}
            items={item.children}
            openByPath={subAccordionToBeOpen}
            onClickTrack={(
              text: string,
              type: "article" | "category" | "sub-category"
            ) => trackAction(text, type)}
          />
        </Accordion>
      );
    }
    return <></>;
  };

  return (
    <>
      <Breadcrumb items={[{ text: rootCategory.title }]} />
      <Title type="default">Navegue pelos temas</Title>
      {categories.map(renderAccordions)}
    </>
  );
}
